/**
 * FocusManager -- Focus Management on Route Navigation
 * Step 9F Green Phase -- Xyden Leadership Assessment Platform
 *
 * WCAG 2.4.3: After route navigation, focus moves to the page heading (h1).
 * Uses a polling approach to handle async page rendering.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export function FocusManager() {
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    if (previousPathRef.current === location.pathname) return;
    previousPathRef.current = location.pathname;

    // Try to focus the h1 element. Retry a few times to handle async rendering.
    let attempts = 0;
    const maxAttempts = 10;
    const intervalMs = 50;

    const tryFocus = () => {
      attempts++;
      const heading = document.querySelector('h1');
      if (heading instanceof HTMLElement) {
        if (!heading.hasAttribute('tabindex')) {
          heading.setAttribute('tabindex', '-1');
        }
        heading.focus();
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(tryFocus, intervalMs);
      } else {
        // Fallback: focus the main content wrapper
        const main = document.querySelector('main');
        if (main instanceof HTMLElement) {
          if (!main.hasAttribute('tabindex')) {
            main.setAttribute('tabindex', '-1');
          }
          main.focus();
        }
      }
    };

    // Initial delay to let the DOM update after route change
    setTimeout(tryFocus, 30);
  }, [location.pathname]);

  return null;
}

export default FocusManager;
